import { Box, Pagination, useToast } from 'braid-design-system';
import { useRef, type FC, type FormEventHandler, useEffect } from 'react';
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { createForm } from '@seek/forms-ui';
import { useTranslations } from '@vocab/react';
import { NoResultForNonFilteredSearch } from './NoResultForNonFilteredSearch';
import { NoResultForFilteredSearch } from './NoResultForFilteredSearch';
import {
  useApplicationAttachmentsLazyQuery,
  useYourCandidatesQuery,
} from './YourCandidates.generated';
import { TableLayout } from '../TableLayout';
import { renderTableBodySkeleton } from '../TableBodySkeleton';
import { HeadingAndSearch } from '../HeadingAndSearch';
import { MainContentLayout } from '../MainContentLayout';
import { CandidateRow } from '../CandidateRow';
import { MainContentError } from '../MainContentError';
import { TrackOnMount, type YourCandidatesPageAnalytics } from '../analytics';
import translations from './.vocab';
import type { AuthN } from '@seek/online-identity';
import { type AttachmentType, downloadAttachment } from './downloadAttachment';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';

export const YourCandidates: FC<{
  authN: AuthN;
  baseApiPath: string;
  pageAnalytics: YourCandidatesPageAnalytics;
}> = ({ baseApiPath, authN, pageAnalytics }) => {
  const { router } = useAppConfig();

  const mainContentRef = useRef<HTMLDivElement>(null);
  const [queryParams, setQueryParams] = useQueryParams({
    page: withDefault(NumberParam, 1),
    q: withDefault(StringParam, ''),
  });
  const { loading, data, error } = useYourCandidatesQuery({
    variables: {
      input: {
        pageNumber: queryParams.page,
        pageSize: 20,
        searchTerm: queryParams.q,
        sort: '-applicationTime', // Sort by most recent application
      },
    },
    fetchPolicy: 'no-cache',
  });
  const { t } = useTranslations(translations);

  const attachmentErrorMessageMap = {
    RESUME: t("Sorry, we can't download the resumé right now."),
    COVER_LETTER: t("Sorry, we can't download the cover letter right now."),
    SELECTION_CRITERIA: t(
      "Sorry, we can't download the selection criteria right now.",
    ),
  } satisfies Record<AttachmentType, string>;

  const showToast = useToast();
  const [fetchApplicationAttachments] = useApplicationAttachmentsLazyQuery();
  const onClickAttachmentType = ({
    prospectId,
    attachmentType,
    attributes,
  }: {
    prospectId: number;
    attachmentType: AttachmentType;
    attributes: {
      pageNumber: number;
      adcentreCandidateId: string | number;
      seekCandidateId: string | number;
      jobId: string | number;
    };
  }) => {
    fetchApplicationAttachments({
      variables: {
        input: {
          prospectId,
        },
      },
      onCompleted: ({ applicationAttachments }) => {
        const typeNameMapping: Record<AttachmentType, string> = {
          RESUME: 'Resume',
          COVER_LETTER: 'Cover Letter',
          SELECTION_CRITERIA: 'Selection Criteria',
        };

        const attachment = applicationAttachments.nodes.find(
          (node) => node.typeName === typeNameMapping[attachmentType],
        );

        if (!attachment) {
          return;
        }

        const { id: attachmentId } = attachment;
        downloadAttachment({
          attachmentType,
          attachmentId,
          authN,
          baseApiPath,
          prospectId,
          router,
        }).then((result) => {
          if (result.success) {
            pageAnalytics.trackLink({
              event: 'your_candidate_tab_attachment_download_pressed',
              attributes: {
                ...attributes,
                prospectId,
                attachmentId: result.attachmentId,
                documentType: result.analyticsAttachmentType,
              },
            });
          } else {
            showToast({
              tone: 'critical',
              message: t('Can we try that again?'),
              description: attachmentErrorMessageMap[attachmentType],
              closeLabel: t('Close'),
            });
          }
        });
      },
    });
  };

  const renderTableSection = () => {
    // TODO: Test if error is cleared when apollo fetches again
    if (
      error ||
      data?.yourCandidates.__typename === 'YourCandidatesPayloadError'
    ) {
      return (
        <TrackOnMount
          trackFn={pageAnalytics.trackView}
          data={{
            event: 'error_page_displayed',
            attributes: { currentPage: 'error_page_displayed' },
          }}
        >
          <MainContentError />
        </TrackOnMount>
      );
    }

    if (loading || !data) {
      return <TableLayout body={renderTableBodySkeleton()} pagination={null} />;
    }

    const isFilterOptionsUsed = queryParams.q !== '';
    const totalPages = data.yourCandidates.totalPages;

    if (data.yourCandidates.edges.length === 0) {
      return (
        <TrackOnMount
          trackFn={pageAnalytics.trackView}
          data={{
            event: 'your_candidate_tab_zero_result_displayed',
            attributes: {
              currentPage: 'your_candidate_tab_zero_result_displayed',
              isCandidateListFiltered: isFilterOptionsUsed,
            },
          }}
        >
          {isFilterOptionsUsed ? (
            <NoResultForFilteredSearch />
          ) : (
            <NoResultForNonFilteredSearch />
          )}
        </TrackOnMount>
      );
    }

    return (
      <TrackOnMount
        trackFn={pageAnalytics.trackView}
        data={{
          event: 'your_candidate_tab_displayed',
          attributes: {
            currentPage: 'your_candidate_tab_displayed',
            isCandidateListFiltered: isFilterOptionsUsed,
            pageNumber: queryParams.page,
            sortMode: 'newest-application',
          },
        }}
      >
        <TableLayout
          body={data.yourCandidates.edges.map(({ node }) => {
            const { prospectId, adcentreCandidateId, candidateId, jobId } =
              node;
            const attributes = {
              pageNumber: queryParams.page,
              adcentreCandidateId,
              seekCandidateId: candidateId,
              jobId: Number(jobId),
            };

            return (
              <CandidateRow
                key={node.prospectId}
                candidate={node}
                onClickCandidateName={() =>
                  pageAnalytics.trackLink({
                    event: 'your_candidate_tab_candidate_name_pressed',
                    attributes,
                  })
                }
                onClickPreviousApplication={() =>
                  pageAnalytics.trackLink({
                    event: 'your_candidate_tab_previous_apps_pressed',
                    attributes,
                  })
                }
                onClickResumeDownload={() => {
                  onClickAttachmentType({
                    prospectId,
                    attachmentType: 'RESUME',
                    attributes,
                  });
                }}
                onClickSelectionCriteriaDownload={() => {
                  onClickAttachmentType({
                    prospectId,
                    attachmentType: 'SELECTION_CRITERIA',
                    attributes,
                  });
                }}
                onClickCoverLetterDownload={() => {
                  onClickAttachmentType({
                    prospectId,
                    attachmentType: 'COVER_LETTER',
                    attributes,
                  });
                }}
                onClickEmailCandidate={() =>
                  pageAnalytics.trackLink({
                    event: 'your_candidate_tab_email_candidate_pressed',
                    attributes,
                  })
                }
              />
            );
          })}
          pagination={
            totalPages > 1 ? (
              <Box display="flex" justifyContent="center">
                <Box background="surface" padding="small">
                  <Pagination
                    page={queryParams.page}
                    pageLabel={(pageNumber) =>
                      t('Go to page $pageNumber', { pageNumber })
                    }
                    total={totalPages}
                    label={t('Candidates pagination')}
                    previousLabel={t('Previous')}
                    nextLabel={t('Next')}
                    linkProps={({ page, type }) => ({
                      href: `#${page}`,
                      onClick: (e) => {
                        const attributes = {
                          isLastPage: page === totalPages,
                          pageNumber: page,
                        };
                        e.preventDefault();
                        if (type === 'next') {
                          pageAnalytics.trackLink({
                            event: 'pagination_next_page_btn_pressed',
                            attributes,
                          });
                        } else if (type === 'previous') {
                          pageAnalytics.trackLink({
                            event: 'pagination_previous_page_btn_pressed',
                            attributes,
                          });
                        } else if (type === 'pageNumber') {
                          pageAnalytics.trackLink({
                            event: 'pagination_numbered_page_btn_pressed',
                            attributes,
                          });
                        }
                        setQueryParams({ page });
                        mainContentRef.current?.scrollIntoView({
                          behavior: 'smooth',
                        });
                      },
                    })}
                  />
                </Box>
              </Box>
            ) : null
          }
        />
      </TrackOnMount>
    );
  };

  return (
    <MainContentLayout
      mainContentRef={mainContentRef}
      searchSection={
        <FormProvider initialValues={{ searchTerm: queryParams.q }}>
          {({ handleSubmit, setFieldValue }) => (
            <HeadingAndSearchForm
              setFieldValue={setFieldValue}
              queryParams={queryParams}
              onSubmit={handleSubmit((values) => {
                pageAnalytics.trackLink({
                  event: 'your_candidate_tab_search_button_pressed',
                  attributes: {
                    searchKeyword: values.searchTerm,
                  },
                });
                setQueryParams({
                  q: values.searchTerm,
                  page: 1,
                });
              })}
            />
          )}
        </FormProvider>
      }
      sortSection={null}
      tableSection={renderTableSection()}
    />
  );
};

const { FormProvider, useField } = createForm<{ searchTerm: string }>(
  'your-candidates',
);

const HeadingAndSearchForm: FC<{
  onSubmit: FormEventHandler<HTMLFormElement>;
  queryParams: { q: string };
  setFieldValue: (key: 'searchTerm', value: string) => void;
}> = ({ onSubmit, queryParams, setFieldValue }) => {
  const searchTermProps = useField({ id: 'searchTerm' });
  useEffect(() => {
    setFieldValue('searchTerm', queryParams.q);
  }, [queryParams.q, setFieldValue]);

  return (
    <form onSubmit={onSubmit}>
      <HeadingAndSearch searchTerm={searchTermProps} />
    </form>
  );
};
